import type { CareFormat } from "@src/constants/filter-constants";
import { callApi, extractApiErrorMessage, hasSuccessFlag } from "@src/utils/api-client";
import { parseDateForCalendarComparison } from "@src/utils/date-utils";
import { getFailureTypeForHttpStatus } from "@src/utils/failure-taxonomy";
import { validateSchedulingRequestParams } from "@src/utils/scheduling-metadata";
import ClientLogsService from "./client-logs.service";
import { buildProviderDatesConfig } from "./configs";
/**
 * Provider Dates Service
 *
 * Service layer for retrieving provider availability dates from Athena Real-Time Availability API.
 * This service interfaces with the internal provider-dates API route.
 */

/**
 * Request interface for provider dates service
 */
export interface ProviderDatesRequest {
  user_uuid?: string;
  practiceId: number;
  departmentIds: number[]; // Array of department IDs
  providerId: number;
  startDate: string; // MM/DD/YYYY format
  endDate: string; // MM/DD/YYYY format
  careFormat: CareFormat;
  user_timezone: string; // Timezone of the user
  speciality?: string; // Optional comma-separated specialities
  daysOfTheWeek?: string; // Optional comma-separated days
  ageServed?: number; // Optional age served
  packageId?: number; // Optional package ID
  minimum_day_time?: string; // Optional minimum datetime with timezone offset
  duration?: number; // Optional duration in minutes
  clinicianType?: string; // Optional clinician type
}

/**
 * Response interface for provider dates service
 */
export interface ProviderDatesResponse {
  success: boolean;
  data?: {
    availableDates: string[];
    transactionId: string;
  };
  error?: string;
}

interface BackendProviderDatesResponse {
  message?: string[] | string;
  transactionID?: string;
  error?: string;
  /** True when `message` is stale fallback-cache data served after an upstream failure. */
  degraded?: boolean;
}

const ISO_DATE_PREFIX_PATTERN = /^(\d{4}-\d{2}-\d{2})(?:T.*)?$/;

const normalizeAvailableDate = (value: string): string | null => {
  const isoDatePrefixMatch = ISO_DATE_PREFIX_PATTERN.exec(value);
  if (isoDatePrefixMatch) {
    const isoDate = isoDatePrefixMatch[1];
    return parseDateForCalendarComparison(isoDate) ? isoDate : null;
  }

  const parsedDate = parseDateForCalendarComparison(value);
  if (!parsedDate) {
    return null;
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Retrieves a list of distinct available dates for a provider
 *
 * @param request - The provider dates request parameters
 * @returns Promise with provider dates response
 *
 * @example
 * ```typescript
 * const response = await getProviderAvailableDates({
 *   practiceId: 123,
 *   departmentIds: [456, 789],
 *   providerId: 101,
 *   startDate: '11/01/2025',
 *   endDate: '11/30/2025',
 *   careFormat: 'in-person',
 *   user_timezone: 'America/New_York',
 *   speciality: 'cardiology,neurology',
 *   daysOfTheWeek: 'Monday,Wednesday,Friday'
 * });
 *
 * if (response.success) {
 *   logger.info({ message: 'Available dates:', availableDates: response.data?.availableDates });
 *   logger.info({ message: 'Transaction ID:', transactionId: response.data?.transactionId });
 * } else {
 *   logger.error({ message: 'Error:', error: response.error });
 * }
 * ```
 */
export async function getProviderAvailableDates(
  request: ProviderDatesRequest,
): Promise<ProviderDatesResponse> {
  try {
    const validationErrors = validateSchedulingRequestParams({
      clinicianType: request.clinicianType,
      duration: request.duration,
    });

    if (validationErrors.length > 0) {
      return {
        success: false,
        error: validationErrors.join(", "),
      };
    }

    // Call the internal API route
    const { data: payload, response } = await callApi(buildProviderDatesConfig(request));
    const isOk = response.ok;

    if (!isOk) {
      const responseError = extractApiErrorMessage(payload, response);
      ClientLogsService.logError(
        request.user_uuid || "unknown",
        "provider-dates.service.getProviderAvailableDates",
        "Provider dates request failed",
        new Error(responseError),
        {
          errorType: getFailureTypeForHttpStatus(response.status),
          dependencyName: "ProviderDatesAPI",
          functionName: "getProviderAvailableDates",
        },
      );
      return {
        success: false,
        error: responseError,
      };
    }

    if (hasSuccessFlag<ProviderDatesResponse>(payload)) {
      return payload;
    }

    if (payload === null) {
      ClientLogsService.logError(
        request.user_uuid || "unknown",
        "provider-dates.service.getProviderAvailableDates",
        "Provider dates service error",
        new Error("Invalid JSON response"),
        {
          errorType: "DependencyFailure",
          dependencyName: "ProviderDatesAPI",
          functionName: "getProviderAvailableDates",
        },
      );
      return { success: false, error: "Invalid JSON response" };
    }

    const backendPayload = payload as BackendProviderDatesResponse;

    if (typeof backendPayload.message === "string" && backendPayload.message.trim()) {
      return {
        success: false,
        error: backendPayload.message,
      };
    }

    if (typeof backendPayload.error === "string" && backendPayload.error.trim()) {
      return {
        success: false,
        error: backendPayload.error,
      };
    }

    const availableDates = Array.isArray(backendPayload.message)
      ? backendPayload.message
          .map((dateValue) => {
            return normalizeAvailableDate(dateValue);
          })
          .filter((dateValue): dateValue is string => {
            return Boolean(dateValue);
          })
      : [];

    if (backendPayload.degraded) {
      // Telemetry only (no UX change): tracks how often browsing data is stale
      // fallback-cache data rather than live, to inform whether a visible
      // degraded-mode indicator is worth adding later.
      ClientLogsService.logWebAction(
        request.user_uuid || "unknown",
        "provider-dates.service.getProviderAvailableDates",
        "degraded_availability_response",
        { transactionId: backendPayload.transactionID || "" },
      );
    }

    return {
      success: true,
      data: {
        availableDates,
        transactionId: backendPayload.transactionID || "",
      },
    };
  } catch (error) {
    // Handle network or unexpected errors
    ClientLogsService.logError(
      request.user_uuid || "unknown",
      "provider-dates.service.getProviderAvailableDates",
      `Provider dates service error`,
      error instanceof Error ? error : new Error("Unknown error"),
      {
        errorType: "DependencyFailure",
        dependencyName: "ProviderDatesAPI",
        functionName: "getProviderAvailableDates",
      },
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : "An unexpected error occurred",
    };
  }
}
